import { isBefore, isSameDay, isValid, startOfDay } from 'date-fns'
import { TZDate } from '@date-fns/tz'
import { EASTERN_TIME_ZONE, formatInZone, ISO_DATE_FORMAT, zonedDateTime } from '~/shared/lib/date'
import type {
  BffCrmScheduleDraftsCreateBodyParams,
  BffCrmScheduleDraftsRetrieveResponse,
  UserProvidersScheduleListResponse,
  ScheduleAppointmentTypesListResponse,
} from '@fireflyhealth/api'

// Validation utility functions

/**
 * The text to show for one validation check: a passing check clears it, and a failing one shows
 * its message only while errors are displayed, leaving the current text otherwise.
 */
export const nextErrorText = (problem: string | null, displayError: boolean, current: string) => {
  if (problem === null) return ''
  return displayError ? problem : current
}

/** Now, read in the clinic's zone so day comparisons are clinic days. */
const nowInEastern = () => new TZDate(new Date(), EASTERN_TIME_ZONE)

export const isShiftStartTimeInPast = (startDate: string, startTime: string): boolean => {
  if (!startDate || !startTime) return false

  // The date and time are wall clock in the clinic's zone, not the viewer's.
  const shiftStartDateTime = zonedDateTime(`${startDate} ${startTime}`)
  // An unparseable value leaves the action enabled, as it did before.
  if (!isValid(shiftStartDateTime)) return false

  const currentDateTime = nowInEastern()

  if (isBefore(startOfDay(shiftStartDateTime), startOfDay(currentDateTime))) return true
  if (isSameDay(shiftStartDateTime, currentDateTime)) {
    return isBefore(shiftStartDateTime, currentDateTime)
  }
  return false
}

export const isShiftExceptionStartDateInPast = (startDateTime: string): boolean => {
  if (!startDateTime) return false

  const exceptionStartDateTime = zonedDateTime(startDateTime)
  if (!isValid(exceptionStartDateTime)) return false

  const currentDateTime = nowInEastern()

  if (isBefore(startOfDay(exceptionStartDateTime), startOfDay(currentDateTime))) return true
  if (isSameDay(exceptionStartDateTime, currentDateTime)) {
    return isBefore(exceptionStartDateTime, currentDateTime)
  }
  return false
}

export const EXCEPTIONSTAB = 'Exceptions'

export const APPROVED_STATUS = 'Approved'
export const REJECTED_STATUS = 'Rejected'
export const REVIEW_PENDING_STATUS = 'Review Pending'

interface DayOfWeek {
  dayOfWeek: number
  fullDayName: string
}

export const TABS = {
  Mo: {
    dayOfWeek: 1,
    fullDayName: 'Monday',
  },
  Tu: {
    dayOfWeek: 2,
    fullDayName: 'Tuesday',
  },
  We: {
    dayOfWeek: 3,
    fullDayName: 'Wednesday',
  },
  Th: {
    dayOfWeek: 4,
    fullDayName: 'Thursday',
  },
  Fr: {
    dayOfWeek: 5,
    fullDayName: 'Friday',
  },
  Sa: {
    dayOfWeek: 6,
    fullDayName: 'Saturday',
  },
  Su: {
    dayOfWeek: 7,
    fullDayName: 'Sunday',
  },
} satisfies Record<string, DayOfWeek>

export type DayTab = keyof typeof TABS
export type ShiftTab = DayTab | typeof EXCEPTIONSTAB

export const FIRST_TAB: DayTab = 'Mo'
export const TAB_KEYS: ShiftTab[] = [...(Object.keys(TABS) as DayTab[]), EXCEPTIONSTAB]

type ScheduleResponse = UserProvidersScheduleListResponse[number]
type ScheduleProviderResponse = ScheduleResponse['provider']
// Wire element shapes pulled from the generated schedule response so the
// read-mapper inputs can't drift from the API contract. mapShift /
// mapShiftException also accept the structurally identical staging_shifts /
// staging_shift_exceptions elements off ingestion_jobs.
type ShiftResponse = NonNullable<ScheduleResponse['shifts']>[number]
type ShiftExceptionResponse = NonNullable<ScheduleResponse['shift_exceptions']>[number]

// ---------------------------------------------------------------------------
// Read working models: the snake_case shapes the mappers below
// produce. They are deliberately NOT pulled from the generated client because
// each normalizes the wire for the UI (field coercions, a synthesized `bounds`,
// or UI-local flags). The per-type notes call out the specific divergence.
// ---------------------------------------------------------------------------

// Mirrors the wire provider but coerces pk -> number and
// allow_edit_schedule -> boolean (both are strings on the wire).
export type Provider = {
  is_taking_new_patients: boolean
  pk: number
  first_name: string | null
  last_name: string | null
  physician: number | null
  allow_edit_schedule: boolean
}

// Half-open date range. The wire range carries only lower/upper; `bounds` is
// synthesized by mapEffectivePeriod, so this can't be the generated type.
type EffectivePeriod = {
  lower: string
  upper: string | null
  bounds: string
}

// Datetime range for exceptions; `upper` is coerced non-null ('' when
// open-ended) so the date inputs and all-day check stay string-typed.
type Period = {
  lower: string
  upper: string
  bounds: string
}

// Post-validation draft-create response: narrows the optional wire `id` to a
// required number so callers can track the new draft (see createScheduleIngestionJob).
export type ScheduleDraftResponse = {
  success: boolean
  message: string
  id: number
}

// Normalized (snake_case) job embedded in Schedule, built from the generated
// ingestion_jobs[]; staging_* reuse mapShift / mapShiftException.
type IngestionJob = {
  review_status: string
  id: number
  staging_shifts: StagingShift[]
  staging_shift_exceptions: StagingShiftException[]
}

// Ingestion-impact model: the typed `ingestion_impact` shape off the generated
// drafts-retrieve response. Every field is directly renderable
// (`affected_patient` non-null, `patient_url` nullable), so no normalization
// layer is needed — alias the wire type so it stays in lockstep with the
// contract. Distinct from the snake_case IngestionJob/StagingShift above, which
// come off getSchedulesForProvider rather than this endpoint.
export type IngestionImpact = BffCrmScheduleDraftsRetrieveResponse['ingestion_impact']

// Normalized staging entries inside IngestionJob (snake_case) — identical to
// Shift / ShiftException minus the UI-local edit flags.
type StagingShift = {
  id: number
  day_of_week: number
  start_time: string
  stop_time: string
  effective_period: EffectivePeriod
}

/**
 * The reasons the schedule-draft endpoint accepts, derived from the contract so the editor's
 * dropdown and the request body cannot drift apart.
 */
export type ShiftExceptionReason = NonNullable<
  NonNullable<BffCrmScheduleDraftsCreateBodyParams['shift_exceptions']>[number]['reason']
>

type StagingShiftException = {
  id: number
  period: Period
  reason: ShiftExceptionReason | null
}

// Rendered shift: wire fields normalized (day_of_week -> number, `bounds`
// added) plus the UI-local `isEdited` flag, which has no wire equivalent.
export type Shift = {
  id: number
  day_of_week: number
  start_time: string
  stop_time: string
  effective_period: EffectivePeriod
  isEdited?: boolean
}

// Rendered exception: normalized period plus the UI-local isEdited / isAllDay
// flags, neither of which exists on the wire.
export type ShiftException = {
  id: number
  period: Period
  reason: ShiftExceptionReason | null
  isEdited?: boolean
  isAllDay?: boolean
}

// day_of_week coerced to number; id is nullable to represent a ratio the UI has
// added but not yet persisted (the wire id is always present).
type VisitMixRatio = {
  id: number | null
  percentage_of_slots: number
  day_of_week: number
}

// Identical to the wire shape; kept as a readable local alias rather than a
// deep indexed-access type into the schedule response.
type VisitMixGroup = {
  id: number
  title: string
}

// Widens the wire's unique_key enum to string. Shared by the standalone list and
// the schedule-embedded copy via mapAppointmentType.
export type AppointmentType = {
  unique_key: string
  name: string
  id: number
  visit_mix_group: VisitMixGroup | null
}

// Pairs an appointment type with its per-day visit-mix ratios.
type AppointmentTypeMapping = {
  visit_mix_ratios: VisitMixRatio[]
  appointment_type: AppointmentType
}

// Top-level normalized model produced by mapSchedule from the generated
// userProvidersSchedule.list response.
export type Schedule = {
  id: number
  provider: Provider
  shifts: Shift[]
  shift_exceptions: ShiftException[]
  ingestion_jobs: IngestionJob[]
  appointment_type_mappings: AppointmentTypeMapping[]
}

// ---------------------------------------------------------------------------
// UI / write form models (no direct generated equivalent).
// ---------------------------------------------------------------------------
// A single visit-mix slider row in the editor.
export interface VisitMix {
  percentage: number
  id: number | null
  isEdited?: boolean
}

export const formatTime = (timeString: string) => {
  const [hourString = '', minute] = timeString.split(':')
  const hour = +hourString % 24
  return (hour % 12 || 12) + ':' + minute + (hour < 12 ? ' am' : ' pm')
}

const formatDate = (dateString: string | null) => {
  if (dateString)
    return new Date(dateString).toLocaleDateString('en-US', {
      timeZone: 'UTC',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  return '–'
}

const periodDateTime = (dateTimeString: string) =>
  formatInZone(dateTimeString, `${ISO_DATE_FORMAT}'T'HH:mm`)

const formatDateWeekday = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    timeZone: EASTERN_TIME_ZONE,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

const formatTimePeriod = (dateTimeString: string) => {
  return new Date(dateTimeString).toLocaleTimeString('en-US', {
    timeZone: EASTERN_TIME_ZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

export const formatEffectivePeriod = ({ lower, upper }: EffectivePeriod) => {
  return `Starts: ${formatDate(lower)}, Ends: ${formatDate(upper)}`
}

export const formatPeriodDate = ({ lower, upper }: Period) => {
  if (periodDateTime(lower).slice(0, 10) === periodDateTime(upper).slice(0, 10))
    return formatDateWeekday(lower)
  return `${formatDateWeekday(lower)} – ${formatDateWeekday(upper)}`
}

export const formatPeriodTime = ({ lower, upper }: Period) => {
  if (periodDateTime(lower).slice(0, 10) === periodDateTime(upper).slice(0, 10))
    if (
      periodDateTime(lower).slice(11, 16) === '00:00' &&
      periodDateTime(upper).slice(11, 16) === '23:59'
    )
      return 'All Day'
    else return `${formatTimePeriod(lower)} – ${formatTimePeriod(upper)}`
  return 'All Day'
}

export const getIsAllDay = ({ lower, upper }: Period) => {
  if (
    periodDateTime(lower).slice(11, 16) === '00:00' &&
    periodDateTime(upper).slice(11, 16) === '23:59'
  )
    return true
  return false
}

export const getShifts = (stagingShifts: StagingShift[]): Shift[] =>
  stagingShifts.map(stagingShift => {
    return {
      id: stagingShift.id,
      day_of_week: stagingShift.day_of_week,
      start_time: stagingShift.start_time,
      stop_time: stagingShift.stop_time,
      effective_period: stagingShift.effective_period,
      isEdited: false,
    }
  })

export const getShiftExceptions = (
  stagingShiftExceptions: StagingShiftException[],
): ShiftException[] =>
  stagingShiftExceptions.map(stagingShiftException => {
    return {
      id: stagingShiftException.id,
      period: stagingShiftException.period,
      reason: stagingShiftException.reason,
      isEdited: false,
      isAllDay: getIsAllDay(stagingShiftException.period),
    }
  })

export const sortShiftFunction = (a: Shift, b: Shift) =>
  a.start_time == b.start_time
    ? a.effective_period.lower < b.effective_period.lower
      ? -1
      : 1
    : a.start_time < b.start_time
      ? -1
      : 1

export const sortShiftExceptionFunction = (a: ShiftException, b: ShiftException) =>
  // compare period lower. If lower is equal, compare the upper
  a.period.lower == b.period.lower
    ? a.period.upper < b.period.upper
      ? -1
      : 1
    : a.period.lower < b.period.lower
      ? -1
      : 1

// Grouped appointment types including those without a group (null group)
export type GroupedAppointmentTypes = {
  group: VisitMixGroup | null
  appointmentTypes: AppointmentType[]
}

/**
 * Groups appointment types by their visit mix group, including those without a group.
 * Appointment types without a group are placed in a "null group" at the end.
 * Results are sorted alphabetically by group title, with null group last.
 */
export const groupAppointmentTypesByVisitMixGroup = (
  appointmentTypes: AppointmentType[],
): GroupedAppointmentTypes[] => {
  const groupMap = new Map<number | null, GroupedAppointmentTypes>()

  appointmentTypes.forEach(apptType => {
    const groupId = apptType.visit_mix_group?.id ?? null
    let grouped = groupMap.get(groupId)
    if (!grouped) {
      grouped = { group: apptType.visit_mix_group ?? null, appointmentTypes: [] }
      groupMap.set(groupId, grouped)
    }
    grouped.appointmentTypes.push(apptType)
  })

  // Sort: groups with id first (sorted by title), then null group at the end
  return Array.from(groupMap.values()).sort((a, b) => {
    if (a.group === null && b.group === null) return 0
    if (a.group === null) return 1
    if (b.group === null) return -1
    return a.group.title.localeCompare(b.group.title)
  })
}

// ---------------------------------------------------------------------------
// read-mappers: normalize the generated (`@fireflyhealth/api`)
// response into the working models above. The generated client returns raw
// snake_case; we coerce the few fields whose wire type diverges from the
// ergonomics the UI relies on (day_of_week -> number, allow_edit_schedule ->
// boolean, provider.pk -> number, period.upper null -> '').
// ---------------------------------------------------------------------------

// The wire types day_of_week as a string enum ("1".."7"); the UI indexes
// weekday tabs/ratios by number, so coerce once here.
const toDayOfWeek = (value: string | number): number => Number(value)

// Coerces the two provider fields whose wire type diverges from what the UI
// relies on: pk -> number (used as a numeric key) and allow_edit_schedule ->
// boolean (drives the edit-enabled state).
const mapProvider = (provider: ScheduleProviderResponse): Provider => ({
  pk: Number(provider.pk),
  first_name: provider.first_name,
  last_name: provider.last_name,
  physician: provider.physician,
  is_taking_new_patients: provider.is_taking_new_patients,
  allow_edit_schedule: Boolean(provider.allow_edit_schedule),
})

// The wire range carries only lower/upper; synthesize the `bounds` the UI's
// date widgets expect (the schedule API always emits half-open periods).
const mapEffectivePeriod = (
  effective_period: ShiftResponse['effective_period'],
): EffectivePeriod => ({
  lower: effective_period.lower,
  upper: effective_period.upper,
  bounds: '[)',
})

// Same `bounds` synthesis as mapEffectivePeriod, plus coerce a null upper to
// '' so the all-day check and the date inputs stay string-typed.
const mapPeriod = (period: ShiftExceptionResponse['period']): Period => ({
  lower: period.lower,
  upper: period.upper ?? '',
  bounds: '[)',
})

// Normalizes a wire shift (day_of_week, effective_period) and stamps the
// UI-local `isEdited` flag, which has no wire equivalent.
const mapShift = (shift: ShiftResponse): Shift => ({
  id: shift.id,
  day_of_week: toDayOfWeek(shift.day_of_week),
  start_time: shift.start_time,
  stop_time: shift.stop_time,
  effective_period: mapEffectivePeriod(shift.effective_period),
  isEdited: false,
})

// Normalizes a wire exception (period, defaulted reason) and derives the
// UI-local `isEdited`/`isAllDay` flags, neither of which is on the wire.
const mapShiftException = (exception: ShiftExceptionResponse): ShiftException => {
  const period = mapPeriod(exception.period)
  return {
    id: exception.id,
    period,
    reason: exception.reason ?? null,
    isEdited: false,
    isAllDay: getIsAllDay(period),
  }
}

// Structural shape shared by the standalone appointment-types list and the
// appointment_type embedded in a schedule's mappings.
type AppointmentTypeResponse = {
  id: number
  name: string
  unique_key: string
  visit_mix_group: VisitMixGroup | null
}

// Projects the appointment-type fields the UI uses so the standalone list and
// the schedule-embedded copy yield identical objects (they converge into one
// array in ProviderShiftSettings and must agree field-for-field).
const mapAppointmentType = (appointmentType: AppointmentTypeResponse): AppointmentType => ({
  id: appointmentType.id,
  name: appointmentType.name,
  unique_key: appointmentType.unique_key,
  visit_mix_group: appointmentType.visit_mix_group,
})

export const mapAppointmentTypes = (
  appointmentTypes: ScheduleAppointmentTypesListResponse,
): AppointmentType[] => appointmentTypes.map(mapAppointmentType)

// Top-level read-mapper: defaults the generated `.optional()` arrays to [] and
// drops mappings whose appointment_type is null, so consumers get a fully
// populated, non-optional Schedule.
export const mapSchedule = (schedule: ScheduleResponse): Schedule => ({
  id: schedule.id,
  provider: mapProvider(schedule.provider),
  shifts: (schedule.shifts ?? []).map(mapShift),
  shift_exceptions: (schedule.shift_exceptions ?? []).map(mapShiftException),
  ingestion_jobs: (schedule.ingestion_jobs ?? []).map(job => ({
    id: job.id,
    review_status: job.review_status ?? '',
    staging_shifts: (job.staging_shifts ?? []).map(mapShift),
    staging_shift_exceptions: (job.staging_shift_exceptions ?? []).map(mapShiftException),
  })),
  appointment_type_mappings: (schedule.appointment_type_mappings ?? []).flatMap(mapping => {
    const appointment_type = mapping.appointment_type
    if (!appointment_type) return []
    return [
      {
        appointment_type: mapAppointmentType(appointment_type),
        visit_mix_ratios: (mapping.visit_mix_ratios ?? []).map(ratio => ({
          id: ratio.id,
          percentage_of_slots: ratio.percentage_of_slots,
          day_of_week: toDayOfWeek(ratio.day_of_week),
        })),
      },
    ]
  }),
})
